import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import type { FitCastBoardContext } from "@/lib/timeline/board/FitCast/context";
import type { PlaybackState } from "@/lib/timeline/playback";
import { applyTimelineStageViewCss } from "@/lib/timeline/view/StageViewCss/css";
import { shouldHoldRestoredCamera } from "@/lib/timeline/view/TimelineSession/cameraRestore";
import type { TimelineViewTransform } from "@/lib/timeline/view/ViewTransform/transform";
import { resolveLockedViewTransform } from "./resolveLockedViewTransform";

interface ResolveStageViewInput {
  layout: BookTimelineLayout;
  cameraLockEnabled: boolean;
  getPlayback: () => PlaybackState;
  stage: HTMLElement | null;
  viewRef: { current: TimelineViewTransform };
  followZoomRef: { current: number };
  lockAutoFitRef: { current: boolean };
  lastFitMsRef: { current: number };
  panBreakRef: { current: boolean };
  restoredHoldRef: { current: boolean };
  getBoardFitContext?: () => FitCastBoardContext | null;
}

/** Saved frame stays put until play, scrub, or a new camera gesture. */
export function resolveStageView(
  input: ResolveStageViewInput,
): TimelineViewTransform {
  const playback = input.getPlayback();
  const holding = shouldHoldRestoredCamera(
    input.restoredHoldRef.current,
    playback.playing,
    playback.scrub,
  );
  input.restoredHoldRef.current = holding;
  if (holding || input.panBreakRef.current) {
    applyTimelineStageViewCss(input.stage, input.viewRef.current);
    return input.viewRef.current;
  }
  if (input.cameraLockEnabled && input.stage !== null) {
    return resolveLockedViewTransform({
      layout: input.layout,
      stage: input.stage,
      getPlayback: input.getPlayback,
      viewRef: input.viewRef,
      followZoomRef: input.followZoomRef,
      lockAutoFitRef: input.lockAutoFitRef,
      lastFitMsRef: input.lastFitMsRef,
      getBoardFitContext: input.getBoardFitContext,
    });
  }
  return input.viewRef.current;
}
